<template>
  <div class="home-container">
    <!-- 顶部信息卡片区域 -->
    <div class="info-cards-section">
      <!-- 最新公告（轮播） -->
      <div class="info-card news-card flex-col">
        <div class="card-header">
          <h3><i class="fas fa-bullhorn"></i> 最新公告</h3>
          <router-link to="/announcements" class="view-all">查看全部</router-link>
        </div>
        <div class="card-content flex-1 flex-col">
          <el-carousel v-if="latestAnnouncements.length > 0"
            :interval="5000"
            arrow="always"
            height="220px"
            indicator-position="none"
            @change="onCarouselChange"
            class="announcement-carousel">
            <el-carousel-item v-for="(announcement, idx) in latestAnnouncements.slice(0, 5)" :key="announcement.id" :name="String(idx)">
              <div class="announcement-card-flex" @click="goToAnnouncementDetail(announcement.id)">
                <div class="news-title top-title">
              <span class="title">{{ announcement.title }}</span>
              <div class="badges">
                    <el-tag :type="getTypeTagType(announcement.type)" size="small">{{ getTypeText(announcement.type) }}</el-tag>
                    <el-tag v-if="announcement.is_pinned" type="danger" size="small">置顶</el-tag>
              </div>
            </div>
                <div class="card-content-center">
                  <img v-if="announcement.home_image" :src="announcement.home_image" class="announcement-img-center-flex" alt="公告图片" />
                  <div v-else class="news-brief">{{ getExcerpt(announcement.content) }}</div>
                </div>
                <div class="news-meta bottom-meta">
              <span class="date">{{ formatDate(announcement.publish_at) }}</span>
              <span class="author">发布者：{{ announcement.author_callsign }}</span>
            </div>
            </div>
            </el-carousel-item>
          </el-carousel>
          <div v-else class="no-announcements">
            <p>暂无公告</p>
          </div>
          <div v-if="latestAnnouncements.length > 0" class="custom-carousel-indicators">
            <span v-for="(item, idx) in latestAnnouncements.slice(0, 5)" :key="item.id"
              :class="['custom-indicator-dot', { active: idx === activeAnnouncementIndex }]">
            </span>
          </div>
        </div>
      </div>

      <!-- 活动日历 -->
      <div class="info-card events-calendar-card">
        <div class="card-header">
          <div class="header-top">
            <h3><i class="fas fa-calendar-alt"></i> 活动日历</h3>
            <div class="calendar-view-tabs">
              <button 
                v-for="view in calendarViews" 
                :key="view.value"
                :class="['view-tab', { active: currentCalendarView === view.value }]"
                @click="currentCalendarView = view.value"
              >
                {{ view.label }}
              </button>
            </div>
          </div>
          <div class="calendar-controls">
            <button @click="previousPeriod" class="calendar-nav-btn">
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="current-period">{{ currentPeriodText }}</span>
            <button @click="nextPeriod" class="calendar-nav-btn">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
        <div class="card-content">
          <!-- 周视图 -->
          <div v-if="currentCalendarView === 'week'" class="calendar-week-view">
            <div class="week-header">
              <div v-for="day in weekDays" :key="day.date" class="week-day-header">
                <div class="day-name">{{ day.name }}</div>
                <div class="day-date">{{ day.date }}</div>
              </div>
            </div>
            <div class="week-grid">
              <div v-for="day in weekDays" :key="day.key" 
                   :class="['week-day-cell', getDayCellClasses(day.fullDate)]"
                   @click="handleDayClick(day.fullDate)">
                <div class="day-content">
                  <div class="day-number">{{ day.date }}</div>
                  <div class="event-star" v-if="getEventsForDate(day.fullDate).length > 0">
                    <i class="fas fa-star"></i>
                  </div>
                  <div class="event-indicators" v-if="getEventsForDate(day.fullDate).length > 0">
                    <div class="event-tooltip">
                      <div v-for="event in getEventsForDate(day.fullDate)" :key="event.id" class="event-tooltip-item">
                        <div class="event-title">{{ event.party_name }}</div>
                        <div class="event-time">{{ formatEventTime(event.party_date, event.party_time, event.party_date_end, event.party_time_end) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 月视图 -->
          <div v-else-if="currentCalendarView === 'month'" class="calendar-month-view">
            <div class="month-grid">
              <div v-for="day in monthDays" :key="day.key" 
                   :class="['month-day-cell', { 'other-month': !day.isCurrentMonth, 'today': day.isToday }, getDayCellClasses(day.fullDate)]"
                   @click="handleDayClick(day.fullDate)">
                <div class="day-content">
                  <div class="day-number">{{ day.dayNumber }}</div>
                  <div class="event-star" v-if="getEventsForDate(day.fullDate).length > 0">
                    <i class="fas fa-star"></i>
                  </div>
                  <div class="event-indicators" v-if="getEventsForDate(day.fullDate).length > 0">
                    <div class="event-tooltip">
                      <div v-for="event in getEventsForDate(day.fullDate)" :key="event.id" class="event-tooltip-item">
                        <div class="event-title">{{ event.party_name }}</div>
                        <div class="event-time">{{ formatEventTime(event.party_date, event.party_time, event.party_date_end, event.party_time_end) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 年视图 -->
          <div v-else-if="currentCalendarView === 'year'" class="calendar-year-view">
            <div class="year-grid">
              <div v-for="month in yearMonths" :key="month.month" class="year-month-cell">
                <div class="month-name">{{ month.name }}</div>
                <div class="month-days">
                  <div v-for="day in getMonthDays(month.month)" :key="`${month.month}-${day.date}`" 
                       :class="['year-day-cell', getDayCellClasses(day.fullDate)]"
                       @click="handleDayClick(day.fullDate)">
                    <div class="day-number">{{ day.dayNumber }}</div>
                    <div class="event-star" v-if="getEventsForDate(day.fullDate).length > 0">
                      <i class="fas fa-star"></i>
                    </div>
                    <div class="event-indicators" v-if="getEventsForDate(day.fullDate).length > 0">
                      <div class="event-tooltip">
                        <div v-for="event in getEventsForDate(day.fullDate)" :key="event.id" class="event-tooltip-item">
                          <div class="event-title">{{ event.party_name }}</div>
                          <div class="event-time">{{ formatEventTime(event.party_date, event.party_time, event.party_date_end, event.party_time_end) }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 最新注册 -->
      <div class="info-card new-users-card">
        <div class="card-header">
          <h3><i class="fas fa-user-plus"></i> 最新注册</h3>
          <router-link to="/users" class="view-all">查看全部</router-link>
        </div>
        <div class="card-content scrollable">
          <!-- 调试信息 -->
          <div v-if="newUsersData.length === 0" class="debug-info">
            <p>暂无最新注册数据 (数据长度: {{ newUsersData.length }})</p>
          </div>
          <div v-for="user in newUsersData.slice(0, 5)" :key="user.id" class="user-item" @click="goToUserProfile(user.id)">
            <img :src="`https://pilot.chinaflier.com/api/v1/data/avatar?pic=true&uid=${user.forum_uid}`" :alt="user.application_id" class="user-avatar">
            <div class="user-info">
              <span class="username">CFR{{ user.application_id }}</span>
              
            </div>
            <span class="register-time">{{ formatTimeAgo(user.reviewed_at) }}</span>
          </div>
        </div>
      </div>

      <!-- 在线管制 -->
      <div class="info-card online-controllers-card">
        <div class="card-header">
          <h3><i class="fas fa-headset"></i> 在线管制</h3>
          <span class="online-count">在线 {{ onlineAtcList.length }} 席位</span>
        </div>
        <div class="card-content scrollable">
          <div v-if="onlineAtcList.length === 0" class="no-controllers">
            <p>暂无在线管制数据</p>
          </div>
          <div v-else v-for="controller in sortedOnlineAtcList" :key="controller.callsign + controller.uid" class="controller-item">
            <img :src="controller.avatar" :alt="controller.callsign" class="controller-avatar">
            <div class="controller-info">
              <div class="controller-name-role">
                <span class="name">{{ controller.callsign }}</span>
                <span class="role">{{ atcTypeMap[controller.type] || controller.type }}</span>
              </div>
              <div class="controller-details">
                <span class="icao">编号: {{ controller.uid }}</span>
                <span class="duration">在线时长: {{ parseFloat(controller.online_hours) }}小时</span>
              </div>
            </div>
            </div>
          </div>
        </div>

      <!-- 职级变更公示 -->
      <div class="info-card permission-logs-card">
        <div class="card-header">
          <h3><i class="fas fa-user-shield"></i> 职级变更公示</h3>
        </div>
        <div class="card-content scrollable">
          <div v-if="permissionLogs.length === 0" class="no-permission-logs">
            <p>暂无职级变更记录</p>
          </div>
          <div v-else>
            <div v-for="log in permissionLogs" :key="log.id" class="permission-log-item">
              <div class="log-main">{{ parseChangeDetail(log) }}</div>
              <div class="log-meta">{{ log.created_at }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 管制公示 -->
      <div class="info-card atc-permission-card">
        <div class="card-header">
          <h3><i class="fas fa-user-cog"></i> 管制公示</h3>
          <el-select v-model="selectedMainPermission" placeholder="选择大权限" style="width: 180px; margin-left: 16px;" filterable>
            <el-option v-for="item in mainPermissionOptions" :key="item" :label="item" :value="item" />
          </el-select>
        </div>
        <div class="card-content scrollable">
          <div v-if="atcPermissionUsers.length === 0" class="no-atc-permission-users">
            <p>暂无拥有该权限的用户</p>
          </div>
          <div v-else>
            <div v-for="user in atcPermissionUsers" :key="user.uid" class="atc-permission-user-item">
              <span class="user-callsign">CFR{{ user.callsign }}</span>
              <template v-if="selectedMainPermission === '所有权限'">
                <span class="user-main-perms">{{ getUserMainPermissions(user) }}</span>
              </template>
              <template v-else>
                <span class="user-sub-perms">{{ getUserSubPermissions(user, selectedMainPermission) }}</span>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- 徽章变更公示 -->
      <div class="info-card badge-change-card">
        <div class="card-header">
          <h3><i class="fas fa-medal"></i> 徽章变更公示</h3>
        </div>
        <div class="card-content scrollable">
          <div v-if="badgeChangeLogs.length === 0" class="no-badge-change-logs">
            <p>暂无徽章变更记录</p>
          </div>
          <div v-else>
            <div v-for="log in badgeChangeLogs" :key="log.id" class="badge-change-log-item" @click="goToUserProfile(log.target_user_id)">
              <div class="badge-info">
                <img :src="log.badge_icon" :alt="log.badge_name" class="badge-icon-small">
                <span class="badge-name">{{ log.badge_name }}</span>
              </div>
              <div class="change-detail">
                <span class="target-user">CFR{{ log.target_callsign }}</span>
                <span class="action-type" :class="log.action">
                  {{ log.action === 'assign' ? '获得' : '失去' }}
                </span>
                <span class="operator">操作者：CFR{{ log.operator_callsign }}</span>
              </div>
              <div class="badge-change-meta">{{ formatTimeAgo(log.created_at) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 处罚公示 -->
      <div class="info-card penalty-card">
        <div class="card-header">
          <h3><i class="fas fa-exclamation-triangle"></i> 处罚公示</h3>
        </div>
        <div class="card-content scrollable">
          <div class="no-penalty-logs">
            <p>暂无处罚记录</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 排行榜和外链推荐区域 -->
    <div class="rankings-external-section">
      <!-- 排行榜区域 -->
      <div class="ranking-card">
        <div class="card-header">
          <h3>
            <i class="fas fa-trophy"></i>
            排行榜
          </h3>
          <div class="tab-nav ranking-tabs">
            <button
              v-for="tab in rankingTabs"
              :key="tab.value"
              :class="['tab-btn', { active: activeRankingTab === tab.value }]"
              @click="activeRankingTab = tab.value"
            >
              <i :class="['fas', tab.icon]"></i>
              {{ tab.label }}
            </button>
          </div>
          <div class="tab-nav time-tabs">
            <button
              v-for="tab in timeTabs"
              :key="tab.value"
              :class="['tab-btn', { active: activeTimeTab === tab.value }]"
              @click="activeTimeTab = tab.value"
            >
              {{ tab.label }}
            </button>
            <!-- 时间范围显示 -->
            <div class="time-range-info" v-if="currentTimeRange">
              <span class="time-range-text">{{ currentTimeRange }}</span>
            </div>
            <!-- 截榜倒计时 -->
            <div class="countdown-info" v-if="countdownText">
              <span class="countdown-text">{{ countdownText }}</span>
            </div>
          </div>
        </div>
        <div class="card-content">
          <div class="chart-container">
            <canvas id="rankingChart"></canvas>
          </div>
          <div class="ranking-list">
            <div v-if="isLoadingRankings" class="loading-rankings">
              <p>正在加载排行榜数据...</p>
            </div>
            <div v-else-if="(activeRankingTab === 'duration' ? rankingData : activityRankingData).length === 0" class="no-rankings">
              <p>暂无排行榜数据</p>
            </div>
            <div
              v-else
              v-for="item in (activeRankingTab === 'duration' ? rankingData : activityRankingData)"
              :key="item.rank"
              class="ranking-item"
              @click="goToUserProfile(item.uid)"
            >
              <div class="rank-number" :class="{ 'top-three': item.rank <= 3 }">
                {{ item.rank }}
              </div>
              <img :src="item.avatar" :alt="item.display_name" class="user-avatar">
              <div class="user-info">
                <span class="username">{{ item.display_name }}</span>
                <span class="score">
                  {{ activeRankingTab === 'duration' ? (item.online_hours || 0) + '小时' : (item.activity_count || 0) + '次' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 外链推荐区域 -->
      <div class="external-links-card">
        <div class="card-header">
          <h3>
            <i class="fas fa-external-link-alt"></i>
            外链推荐
          </h3>
          <router-link to="/external-links/submit" class="view-all">
            <i class="fas fa-plus"></i>
            提交外链
          </router-link>
        </div>
        <div class="card-content">
          <div v-if="isLoadingExternalLinks" class="loading-external-links">
            <p>正在加载外链数据...</p>
          </div>
          <div v-else-if="externalLinksData.length === 0" class="no-external-links">
            <p>暂无外链推荐</p>
            <router-link to="/external-links/submit" class="submit-link-btn">
              <i class="fas fa-plus"></i>
              提交第一个外链
            </router-link>
          </div>
          <div v-else class="external-links-list">
            <div
              v-for="link in externalLinksData"
              :key="link.id"
              class="external-link-item"
              @click="handleExternalLinkClick(link)"
            >
              <div class="link-header">
                <div class="link-category">
                  <i :class="getCategoryIcon(link.category)"></i>
                  {{ getCategoryLabel(link.category) }}
                </div>
                <div class="link-points">
                  <i class="fas fa-coins"></i>
                  {{ link.points_per_hour }}/小时
                </div>
              </div>
              <div class="link-title">{{ link.title }}</div>
              <div v-if="link.description" class="link-description">{{ link.description }}</div>
              <div class="link-meta">
                <span class="link-author">CFR{{ link.user_callsign }}</span>
                <span class="link-views">
                  <i class="fas fa-eye"></i>
                  {{ link.view_count }}
                </span>
                <span class="link-time">{{ formatTimeAgo(link.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 教程外链区域 -->
    <div class="tutorial-section">
      <div class="tutorial-card">
        <div class="card-header">
          <h3>
            <i class="fas fa-graduation-cap"></i>
            管制教程
          </h3>
          <a href="https://space.bilibili.com/13412396/lists/1146846?type=series" 
             target="_blank" 
             class="view-all"
             title="在B站查看完整教程">
            <i class="fas fa-external-link-alt"></i>
            查看完整教程
          </a>
        </div>
        <div class="card-content">
          <div class="tutorial-iframe-container">
            <iframe 
              src="https://space.bilibili.com/13412396/lists/1146846?type=series"
              frameborder="0"
              allowfullscreen
              class="tutorial-iframe"
              title="管制教程"
              loading="lazy">
            </iframe>
          </div>
          <div class="tutorial-info">
            <div class="tutorial-description">
              <i class="fas fa-info-circle"></i>
              <span>B站优质管制教程合集，包含从入门到进阶的完整学习内容</span>
            </div>
            <div class="tutorial-stats">
              <span class="stat-item">
                <i class="fas fa-play-circle"></i>
                视频教程
              </span>
              <span class="stat-item">
                <i class="fas fa-clock"></i>
                持续更新
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// 声明Chart类型
declare global {
  interface Window {
    Chart: any
  }
}

import { ref, onMounted, onBeforeUnmount, watch, nextTick, computed } from 'vue'
import { publicAnnouncementsAPI, Announcement } from '../api/announcements'
import { homeAPI } from '../api'
import { ElCarousel, ElCarouselItem } from 'element-plus'
import { useRouter } from 'vue-router'
import axios from 'axios'
import api from '../api' // 新增：引入带token的api实例
import { getPilotPermissionsList } from '../api/pilot-permissions' // 新增
import { rankingsAPI, RankingItem } from '../api/rankings' // 新增：引入排行榜API
import { externalLinksAPI, ExternalLink, getCategoryLabel, getCategoryIcon } from '../api/external-links' // 新增：引入外链API

// 定义最新注册数据类型
interface LatestRegistration {
  id: number
  application_id: string
  forum_uid: string
  qq: string
  email: string
  register_date: string
  reviewed_at: string
}

// 定义API响应类型
interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

// 使用从API导入的RankingItem类型

interface PermissionLog {
  id: number
  operator_id: string
  operator_name: string | null
  target_pilot_id: string
  target_name: string | null
  change_detail: string
  created_at: string
}

interface BadgeChangeLog {
  id: number
  operator_id: number
  target_callsign: string
  badge_id: number
  action: 'assign' | 'revoke'
  reason: string
  ip_address: string
  user_agent: string
  created_at: string
  badge_name: string
  badge_icon: string
  operator_callsign: string
}

const activeTab = ref('weekly')
const permissionLogs = ref<PermissionLog[]>([])
const badgeChangeLogs = ref<BadgeChangeLog[]>([])
const activeRankingTab = ref('duration')
const activeTimeTab = ref('weekly')
const durationChartRef = ref(null)
const activityChartRef = ref(null)
let durationChart: any = null
let activityChart: any = null

const tabs = [
  { label: '本周', value: 'weekly' },
  { label: '本月', value: 'monthly' },
  { label: '本年', value: 'yearly' }
]

const rankingTabs = [
  { label: '在线时长', value: 'duration', icon: 'fa-clock' },
  { label: '活动参与', value: 'activity', icon: 'fa-calendar-check' }
]

const timeTabs = [
  { label: '本周', value: 'weekly' },
  { label: '本月', value: 'monthly' },
  { label: '本年', value: 'yearly' }
]

const defaultAvatar = '/assets/images/shortcut.svg'
const defaultEventImage = '/assets/images/shortcut.svg'

// 最新公告数据
const latestAnnouncements = ref<Announcement[]>([])

// 最新注册数据
const newUsersData = ref<LatestRegistration[]>([])

// 最新活动数据
const latestEvents = ref<any[]>([])

// 在线管制数据类型
interface OnlineAtcItem {
  callsign: string;
  uid: string;
  freq: string;
  facility: number;
  lat: number;
  lng: number;
  online_hours: string;
  avatar: string;
  type: string;
}


// 在线管制数据
const onlineAtcList = ref<OnlineAtcItem[]>([])

// 席位类型中文
const atcTypeMap: Record<string, string> = {
  '放行': '放行席位',
  '地面': '地面席位',
  '塔台': '塔台席位',
  '终端': '终端席位',
  '区域': '区域席位',
  '监管': '监管席位',
  '情报': '情报席位',
  '其他': '其他席位'
}

// 席位类型排序
const atcTypeOrder = ['放行', '地面', '塔台', '终端', '区域', '监管', '情报', '其他']

const sortedOnlineAtcList = computed(() => {
  return [...onlineAtcList.value].sort((a, b) => {
    const aIdx = atcTypeOrder.indexOf(a.type)
    const bIdx = atcTypeOrder.indexOf(b.type)
    return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx)
  })
})

const fetchLatestRegistrations = async () => {
  try {
    console.log('开始获取最新注册...')
    const response = await homeAPI.getLatestRegistrations() as any
    console.log('最新注册API响应:', response)
    if (response.success) {
      newUsersData.value = response.data
      console.log('最新注册数据已更新:', newUsersData.value)
    } else {
      console.error('API返回失败:', response)
    }
  } catch (error) {
    console.error('获取最新注册失败:', error)
  }
}

// 获取最新活动
const fetchLatestEvents = async () => {
  try {
    console.log('开始获取最新活动...')
    const response = await homeAPI.getLatestEvents() as any
    console.log('最新活动API响应:', response)
    if (response.success) {
      latestEvents.value = response.data
      console.log('最新活动数据已更新:', latestEvents.value)
    } else {
      console.error('API返回失败:', response)
    }
  } catch (error) {
    console.error('获取最新活动失败:', error)
    // 添加测试数据
    latestEvents.value = [
      {
        id: 1,
        party_name: '测试活动1',
        party_date: '2024-12-20',
        party_time: '14:00',
        party_date_end: '2024-12-20',
        party_time_end: '18:00',
        location: '北京首都国际机场'
      },
      {
        id: 2,
        party_name: '测试活动2',
        party_date: '2024-12-21',
        party_time: '19:00',
        party_date_end: '2024-12-21',
        party_time_end: '21:00',
        location: '上海虹桥国际机场'
      }
    ]
    console.log('使用测试数据:', latestEvents.value)
  }
}

// 获取最新公告
const fetchLatestAnnouncements = async () => {
  try {
    const response = await publicAnnouncementsAPI.getLatest()
    latestAnnouncements.value = response.data
  } catch (error) {
    console.error('获取最新公告失败:', error)
  }
}

// 获取在线管制数据
const fetchOnlineAtc = async () => {
  try {
    const resp = await fetch('/api/online-atc')
    const json = await resp.json()
    if (json.success) {
      onlineAtcList.value = json.data
      // 移除日志输出
    } else {
      onlineAtcList.value = []
    }
  } catch (e) {
    onlineAtcList.value = []
  }
}

// 获取职级变更公示
const fetchPermissionLogs = async () => {
  try {
    const resp = await axios.get('/api/pilot-permission-logs?limit=15')
    if (resp.data && Array.isArray(resp.data.data)) {
      permissionLogs.value = resp.data.data
    }
  } catch (e) {
    permissionLogs.value = []
  }
}

// 获取徽章变更日志
const fetchBadgeChangeLogs = async () => {
  try {
    const response = await homeAPI.getBadgeChangeLogs() as any
    if (response.success) {
      badgeChangeLogs.value = response.data
    } else {
      badgeChangeLogs.value = []
    }
  } catch (error) {
    console.error('获取徽章变更日志失败:', error)
    badgeChangeLogs.value = []
  }
}

function parseChangeDetail(log: PermissionLog) {
  const details = log.change_detail.split('，').map(s => s.trim()).filter(Boolean)
  const give: string[] = []
  const cancel: string[] = []
  details.forEach(item => {
    const match = item.match(/^(.+?)：([01])→([01])$/)
    if (match) {
      const perm = match[1]
      const from = match[2]
      const to = match[3]
      if (from === '0' && to === '1') give.push(perm)
      if (from === '1' && to === '0') cancel.push(perm)
    }
  })
  let msg = `CFR${log.target_name || log.target_pilot_id}被管制教员CFR${log.operator_name || log.operator_id}`
  if (give.length && cancel.length) {
    msg += `给予${give.join('，')}权限，取消${cancel.join('，')}权限`
  } else if (give.length) {
    msg += `给予${give.join('，')}权限`
  } else if (cancel.length) {
    msg += `取消${cancel.join('，')}权限`
  } else {
    msg += `变更了权限`
  }
  return msg
}

// 公告相关函数
const getTypeText = (type: string) => {
  switch (type) {
    case 'general': return '一般';
    case 'important': return '重要';
    case 'urgent': return '紧急';
    case 'maintenance': return '维护';
    default: return type;
  }
}

const getTypeTagType = (type: string) => {
  switch (type) {
    case 'general': return undefined;
    case 'important': return 'warning';
    case 'urgent': return 'danger';
    case 'maintenance': return 'info';
    default: return undefined;
  }
}

const getExcerpt = (content: string, maxLength: number = 80) => {
  if (!content) return '';
  // 去除所有HTML标签
  const text = content.replace(/<[^>]+>/g, '');
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
}

const formatDate = (date: string | null) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

const formatDateTime = (datetime: string) => {
  return new Date(datetime).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatTimeAgo = (datetime: string) => {
  const now = new Date()
  const time = new Date(datetime)
  const diff = now.getTime() - time.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0) return `${days}天前`
  if (hours > 0) return `${hours}小时前`
  if (minutes > 0) return `${minutes}分钟前`
  return '刚刚'
}

const formatDuration = (hours: number) => {
  return `${hours}小时`
}

const rankingData = ref<RankingItem[]>([])
const activityRankingData = ref<RankingItem[]>([])
const isLoadingRankings = ref(false)

// 外链推荐数据
const externalLinksData = ref<ExternalLink[]>([])
const isLoadingExternalLinks = ref(false)

// 时间范围和倒计时相关变量
const currentTimeRange = ref<string>('')
const countdownText = ref<string>('')
const countdownTimer = ref<NodeJS.Timeout | null>(null)

// 日历相关变量
const currentCalendarView = ref<'week' | 'month' | 'year'>('week')
const currentDate = ref(new Date())
const calendarViews = [
  { label: '周', value: 'week' as const },
  { label: '月', value: 'month' as const },
  { label: '年', value: 'year' as const }
]

const eventsData = ref([
  {
    id: 1,
    title: '2024年春季联合管制活动',
    image: defaultEventImage,
    startTime: '2024-03-20 14:00',
    endTime: '2024-03-20 18:00',
    location: '北京首都国际机场',
    airspace: 'ZBAA_CTR'
  },
  {
    id: 2,
    title: '新手管制员培训活动',
    image: defaultEventImage,
    startTime: '2024-03-25 19:00',
    endTime: '2024-03-25 21:00',
    location: '上海虹桥国际机场',
    airspace: 'ZSSS_APP'
  }
])

const initChart = () => {
  console.log('Initializing chart...') // 调试日志
  const ctx = document.getElementById('rankingChart')
  if (!ctx) {
    console.error('Canvas element not found!') // 调试日志
    return
  }

  const currentData = activeRankingTab.value === 'duration' ? rankingData.value : activityRankingData.value
  const labels = currentData.map(item => item.display_name)
  const data = currentData.map(item => activeRankingTab.value === 'duration' ? item.online_hours : item.activity_count)
  const backgroundColor = activeRankingTab.value === 'duration' 
    ? 'rgba(54, 162, 235, 0.8)'  // 蓝色
    : 'rgba(255, 99, 132, 0.8)'  // 红色

  if (durationChart) {
    console.log('Destroying existing chart...') // 调试日志
    durationChart.destroy()
  }

  try {
    console.log('Creating new chart...') // 调试日志
    durationChart = new window.Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: activeRankingTab.value === 'duration' ? '在线时长（小时）' : '活动参与（次）',
          data,
          backgroundColor,
          borderColor: activeRankingTab.value === 'duration'
            ? 'rgba(54, 162, 235, 1)'
            : 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.6,
          categoryPercentage: 0.8
        }]
      },
      options: {
        indexAxis: 'y', // 设置为横向柱状图
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 1000,
          easing: 'easeOutQuart'
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context: any) {
                const value = context.raw
                return activeRankingTab.value === 'duration'
                  ? `在线时长：${value}小时`
                  : `参与次数：${value}次`
              }
            }
          }
        },
        scales: {
          x: { // 原来的y轴变成x轴
            beginAtZero: true,
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.1)'
            },
            ticks: {
              callback: function(value: any) {
                return activeRankingTab.value === 'duration'
                  ? value + 'h'
                  : value + '次'
              }
            }
          },
          y: { // 原来的x轴变成y轴
            grid: {
              display: false
            }
          }
        }
      }
    })
    console.log('Chart created successfully!') // 调试日志
  } catch (error) {
    console.error('Error creating chart:', error) // 调试日志
  }
}

// 监听标签切换
watch([activeRankingTab, activeTimeTab], async () => {
  console.log('Tab changed:', { activeRankingTab: activeRankingTab.value, activeTimeTab: activeTimeTab.value }) // 调试日志
  await fetchRankingsData()
  nextTick(() => {
    initChart()
  })
})

// 监听窗口大小变化
const handleResize = () => {
  if (durationChart) {
    durationChart.resize()
  }
}

// 格式化活动时间
const formatEventTime = (date: string, time: string, dateEnd: string, timeEnd: string) => {
  if (!date || !time) return ''
  // 03/20 14:00 - 03/20 18:00
  const pad = (n: number) => n.toString().padStart(2, '0')
  const [m1, d1] = date.split('-').slice(1).map(Number)
  const [m2, d2] = dateEnd ? dateEnd.split('-').slice(1).map(Number) : [m1, d1]
  return `${pad(m1)}/${pad(d1)} ${time} - ${pad(m2)}/${pad(d2)} ${timeEnd}`
}

// 格式化空域，多个用顿号，超长用省略号
const formatAirspace = (airspace: string) => {
  if (!airspace) return ''
  const arr = airspace.split(/[,，、\s]+/).filter(Boolean)
  const max = 3
  if (arr.length > max) {
    return arr.slice(0, max).join('、') + '...'
  }
  return arr.join('、')
}

const router = useRouter()

// 跳转到用户个人信息页面
function goToUserProfile(userId: number) {
  router.push(`/profile/${userId}`)
}

function goToAnnouncementDetail(id: number) {
  router.push(`/announcements/${id}`)
}

const activeAnnouncementIndex = ref(0)
function onCarouselChange(idx: number) {
  activeAnnouncementIndex.value = idx
}

const mainPermissionOptions = ref<string[]>([])
const selectedMainPermission = ref('所有权限')
const atcPermissionUsers = ref<any[]>([])

const fetchMainPermissions = async () => {
  // 只显示大权限
  mainPermissionOptions.value = [
    '所有权限',
    '塔台权限', '地面权限', '终端权限', '区域权限', '教员权限', '管理员权限', '观察员权限'
  ]
}
const fetchAtcPermissionUsers = async () => {
  try {
    // 用api方法获取所有用户及权限
    const res = await getPilotPermissionsList({ limit: 1000 });
    const all = res.success ? res.data.list : [];
    if (selectedMainPermission.value === '所有权限') {
      // 只要有任意main_permission不是无权限且value为1
      atcPermissionUsers.value = all.filter((user: any) =>
        user.atc_level > 0 && user.permissions.some((p: any) => p.value && p.main_permission !== '无权限')
      ).sort((a: any, b: any) => b.atc_level - a.atc_level);
    } else {
      // 只要有该main_permission下任意sub_permission为1
      atcPermissionUsers.value = all.filter((user: any) =>
        user.atc_level > 0 && user.permissions.some((p: any) => p.value && p.main_permission === selectedMainPermission.value)
      ).sort((a: any, b: any) => b.atc_level - a.atc_level);
    }
  } catch (e) {
    atcPermissionUsers.value = [];
  }
}

// 获取在线时长排行榜数据
const fetchOnlineDurationRankings = async () => {
  try {
    isLoadingRankings.value = true
    console.log('开始获取在线时长排行榜，时间周期:', activeTimeTab.value)
    const response = await rankingsAPI.getOnlineDurationRankings(activeTimeTab.value, 10) as any
    console.log('在线时长排行榜响应:', response)
    if (response && response.success) {
      rankingData.value = response.data.rankings
      console.log('在线时长排行榜数据已更新:', rankingData.value)
    } else {
      console.error('获取在线时长排行榜失败:', response?.message || '未知错误')
      rankingData.value = []
    }
  } catch (error) {
    console.error('获取在线时长排行榜失败:', error)
    rankingData.value = []
  } finally {
    isLoadingRankings.value = false
  }
}

// 获取活动参与排行榜数据
const fetchActivityParticipationRankings = async () => {
  try {
    isLoadingRankings.value = true
    console.log('开始获取活动参与排行榜，时间周期:', activeTimeTab.value)
    const response = await rankingsAPI.getActivityParticipationRankings(activeTimeTab.value, 10) as any
    console.log('活动参与排行榜响应:', response)
    if (response && response.success) {
      activityRankingData.value = response.data.rankings
      console.log('活动参与排行榜数据已更新:', activityRankingData.value)
    } else {
      console.error('获取活动参与排行榜失败:', response?.message || '未知错误')
      activityRankingData.value = []
    }
  } catch (error) {
    console.error('获取活动参与排行榜失败:', error)
    activityRankingData.value = []
  } finally {
    isLoadingRankings.value = false
  }
}

// 获取外链推荐数据
const fetchExternalLinks = async () => {
  try {
    isLoadingExternalLinks.value = true
    const response = await externalLinksAPI.getList({ limit: 5 }) as any
    if (response && response.success) {
      externalLinksData.value = response.data
    } else {
      externalLinksData.value = []
    }
  } catch (error) {
    console.error('获取外链推荐失败:', error)
    externalLinksData.value = []
  } finally {
    isLoadingExternalLinks.value = false
  }
}

// 处理外链点击
const handleExternalLinkClick = async (link: ExternalLink) => {
  try {
    // 增加浏览次数
    await externalLinksAPI.addView(link.id)
    // 在新窗口打开链接
    window.open(link.url, '_blank')
  } catch (error) {
    console.error('处理外链点击失败:', error)
    // 即使增加浏览次数失败，也要打开链接
    window.open(link.url, '_blank')
  }
}

// 根据当前选中的排行榜类型获取数据
const fetchRankingsData = async () => {
  if (activeRankingTab.value === 'duration') {
    await fetchOnlineDurationRankings()
  } else {
    await fetchActivityParticipationRankings()
  }
}

// 计算时间范围显示
const calculateTimeRange = () => {
  const now = new Date()
  let startDate: Date
  let endDate: Date
  
  switch (activeTimeTab.value) {
    case 'weekly':
      // 本周开始时间（周一00:00:00）
      const weekStart = new Date()
      const dayOfWeek = weekStart.getDay() // 0是周日，1是周一
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      weekStart.setDate(weekStart.getDate() - daysToSubtract)
      weekStart.setHours(0, 0, 0, 0)
      startDate = weekStart
      
      // 本周结束时间（周日23:59:59）
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekEnd.getDate() + 6)
      weekEnd.setHours(23, 59, 59, 999)
      endDate = weekEnd
      break
      
    case 'monthly':
      // 本月开始时间（1号00:00:00）
      const monthStart = new Date()
      const currentDay = monthStart.getDate()
      
      // 如果当前是月初（1-3号），显示上个月的数据
      if (currentDay <= 3) {
        monthStart.setMonth(monthStart.getMonth() - 1, 1)
      } else {
        monthStart.setDate(1)
      }
      monthStart.setHours(0, 0, 0, 0)
      startDate = monthStart
      
      // 本月结束时间（月末23:59:59）
      const monthEnd = new Date(monthStart)
      monthEnd.setMonth(monthEnd.getMonth() + 1, 0)
      monthEnd.setHours(23, 59, 59, 999)
      endDate = monthEnd
      break
      
    case 'yearly':
      // 本年开始时间（1月1号00:00:00）
      const yearStart = new Date()
      yearStart.setMonth(0, 1)
      yearStart.setHours(0, 0, 0, 0)
      startDate = yearStart
      
      // 本年结束时间（12月31日23:59:59）
      const yearEnd = new Date(yearStart)
      yearEnd.setMonth(11, 31)
      yearEnd.setHours(23, 59, 59, 999)
      endDate = yearEnd
      break
      
    default:
      startDate = now
      endDate = now
  }
  
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('zh-CN', {
      month: '2-digit',
      day: '2-digit'
    })
  }
  
  currentTimeRange.value = `${formatDate(startDate)} - ${formatDate(endDate)}`
}

// 计算截榜倒计时
const calculateCountdown = () => {
  const now = new Date()
  let nextPeriodStart: Date
  
  switch (activeTimeTab.value) {
    case 'weekly':
      // 下周一00:00:00
      const nextMonday = new Date()
      const daysUntilMonday = (8 - nextMonday.getDay()) % 7
      nextMonday.setDate(nextMonday.getDate() + daysUntilMonday)
      nextMonday.setHours(0, 0, 0, 0)
      nextPeriodStart = nextMonday
      break
      
    case 'monthly':
      // 下个月1号00:00:00
      const nextMonth = new Date()
      nextMonth.setMonth(nextMonth.getMonth() + 1, 1)
      nextMonth.setHours(0, 0, 0, 0)
      nextPeriodStart = nextMonth
      break
      
    case 'yearly':
      // 下一年1月1号00:00:00
      const nextYear = new Date()
      nextYear.setFullYear(nextYear.getFullYear() + 1, 0, 1)
      nextYear.setHours(0, 0, 0, 0)
      nextPeriodStart = nextYear
      break
      
    default:
      nextPeriodStart = now
  }
  
  const timeDiff = nextPeriodStart.getTime() - now.getTime()
  
  if (timeDiff <= 0) {
    countdownText.value = ''
    return
  }
  
  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000)
  
  if (days > 0) {
    countdownText.value = `将在${days}天${hours}小时${minutes}分${seconds}秒后截榜`
  } else if (hours > 0) {
    countdownText.value = `将在${hours}小时${minutes}分${seconds}秒后截榜`
  } else if (minutes > 0) {
    countdownText.value = `将在${minutes}分${seconds}秒后截榜`
  } else {
    countdownText.value = `将在${seconds}秒后截榜`
  }
}

// 启动倒计时定时器
const startCountdownTimer = () => {
  // 清除之前的定时器
  if (countdownTimer.value) {
    clearInterval(countdownTimer.value)
  }
  
  // 立即计算一次
  calculateTimeRange()
  calculateCountdown()
  
  // 每秒更新倒计时
  countdownTimer.value = setInterval(() => {
    calculateCountdown()
  }, 1000)
}

// 停止倒计时定时器
const stopCountdownTimer = () => {
  if (countdownTimer.value) {
    clearInterval(countdownTimer.value)
    countdownTimer.value = null
  }
}

// 日历相关计算属性和函数
const currentPeriodText = computed(() => {
  const date = currentDate.value
  switch (currentCalendarView.value) {
    case 'week':
      const weekStart = new Date(date)
      const dayOfWeek = weekStart.getDay()
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      weekStart.setDate(weekStart.getDate() - daysToSubtract)
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekEnd.getDate() + 6)
      return `${weekStart.getMonth() + 1}月${weekStart.getDate()}日 - ${weekEnd.getMonth() + 1}月${weekEnd.getDate()}日`
    case 'month':
      return `${date.getFullYear()}年${date.getMonth() + 1}月`
    case 'year':
      return `${date.getFullYear()}年`
    default:
      return ''
  }
})

const weekDays = computed(() => {
  const date = currentDate.value
  const weekStart = new Date(date)
  const dayOfWeek = weekStart.getDay()
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  weekStart.setDate(weekStart.getDate() - daysToSubtract)
  
  const days = []
  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart)
    day.setDate(day.getDate() + i)
    days.push({
      name: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][day.getDay()],
      date: day.getDate(),
      fullDate: day.toISOString().split('T')[0],
      key: `week-${day.toISOString().split('T')[0]}` // 添加唯一key
    })
  }
  return days
})

const monthDays = computed(() => {
  const date = currentDate.value
  const year = date.getFullYear()
  const month = date.getMonth()
  
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const startDate = new Date(firstDay)
  startDate.setDate(startDate.getDate() - firstDay.getDay())
  
  const days = []
  const today = new Date()
  
  for (let i = 0; i < 42; i++) {
    const day = new Date(startDate)
    day.setDate(startDate.getDate() + i)
    
    days.push({
      dayNumber: day.getDate(),
      date: day.getDate().toString(),
      fullDate: day.toISOString().split('T')[0],
      isCurrentMonth: day.getMonth() === month,
      isToday: day.toDateString() === today.toDateString(),
      key: `month-${day.toISOString().split('T')[0]}` // 添加唯一key
    })
  }
  return days
})

const yearMonths = computed(() => {
  const date = currentDate.value
  const year = date.getFullYear()
  
  const months = []
  for (let i = 0; i < 12; i++) {
    months.push({
      month: i,
      name: `${i + 1}月`
    })
  }
  return months
})

// 日历导航函数
const previousPeriod = () => {
  const date = new Date(currentDate.value)
  switch (currentCalendarView.value) {
    case 'week':
      date.setDate(date.getDate() - 7)
      break
    case 'month':
      date.setMonth(date.getMonth() - 1)
      break
    case 'year':
      date.setFullYear(date.getFullYear() - 1)
      break
  }
  currentDate.value = date
}

const nextPeriod = () => {
  const date = new Date(currentDate.value)
  switch (currentCalendarView.value) {
    case 'week':
      date.setDate(date.getDate() + 7)
      break
    case 'month':
      date.setMonth(date.getMonth() + 1)
      break
    case 'year':
      date.setFullYear(date.getFullYear() + 1)
      break
  }
  currentDate.value = date
}

// 获取指定日期的活动
const getEventsForDate = (dateStr: string) => {
  return latestEvents.value.filter(event => {
    const eventDate = event.party_date
    return eventDate === dateStr
  })
}

// 获取指定月份的活动
const getEventsForMonth = (month: number) => {
  return latestEvents.value.filter(event => {
    const eventDate = new Date(event.party_date)
    return eventDate.getMonth() === month
  })
}

// 获取指定月份的日期
const getMonthDays = (month: number) => {
  const date = currentDate.value
  const year = date.getFullYear()
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  
  const days = []
  for (let i = 1; i <= lastDay.getDate(); i++) {
    const day = new Date(year, month, i)
    days.push({
      dayNumber: i,
      date: `${month}-${i}`, // 使用月份-日期作为唯一key
      fullDate: day.toISOString().split('T')[0]
    })
  }
  return days
}

// 获取活动状态
const getEventStatus = (event: any) => {
  const now = new Date()
  const eventStart = new Date(`${event.party_date}T${event.party_time}`)
  const eventEnd = new Date(`${event.party_date_end}T${event.party_time_end}`)
  
  if (now < eventStart) {
    return 'upcoming' // 未开始 - 蓝色
  } else if (now >= eventStart && now <= eventEnd) {
    return 'ongoing' // 进行中 - 绿色
  } else {
    return 'ended' // 已结束 - 红色
  }
}

// 跳转到活动详情
const goToEventDetail = (eventId: number) => {
  router.push(`/events/${eventId}`)
}

// 获取日期格子的CSS类
const getDayCellClasses = (dateStr: string) => {
  const events = getEventsForDate(dateStr)
  if (events.length === 0) {
    return {}
  }
  
  const classes: Record<string, boolean> = {
    'has-events': true
  }
  
  // 检查是否有不同类型的活动
  const hasUpcoming = events.some(event => getEventStatus(event) === 'upcoming')
  const hasOngoing = events.some(event => getEventStatus(event) === 'ongoing')
  const hasEnded = events.some(event => getEventStatus(event) === 'ended')
  
  if (hasUpcoming) classes['has-upcoming'] = true
  if (hasOngoing) classes['has-ongoing'] = true
  if (hasEnded) classes['has-ended'] = true
  
  return classes
}

// 处理日期格子点击
const handleDayClick = (dateStr: string) => {
  const events = getEventsForDate(dateStr)
  if (events.length > 0) {
    // 如果有多个活动，跳转到第一个
    goToEventDetail(events[0].id)
  }
}


watch(selectedMainPermission, fetchAtcPermissionUsers)

// 监听时间标签变化，更新时间范围和倒计时
watch(activeTimeTab, () => {
  startCountdownTimer() // 重新启动倒计时定时器
})

// 管制公示卡片内容渲染辅助
function getUserMainPermissions(user: any): string {
  // 获取该用户所有大权限（去重，非无权限，value为1）
  const mains = Array.from(new Set(user.permissions.filter((p: any) => p.value && p.main_permission !== '无权限').map((p: any) => p.main_permission)))
  if (mains.length > 3) {
    return mains.slice(0, 3).join('、') + ' 等'
  }
  return mains.join('、')
}
function getUserSubPermissions(user: any, main: string): string {
  // 获取该用户该大权限下所有小权限（value为1）
  return user.permissions.filter((p: any) => p.value && p.main_permission === main).map((p: any) => p.sub_permission).join('、')
}

onMounted(async () => {
  console.log('Component mounted') // 调试日志
  fetchLatestAnnouncements() // 获取最新公告
  fetchLatestRegistrations() // 获取最新注册
  fetchLatestEvents() // 获取最新活动
  fetchOnlineAtc() // 获取在线管制数据
  fetchPermissionLogs() // 获取职级变更公示
  fetchBadgeChangeLogs() // 获取徽章变更日志
  fetchMainPermissions()
  fetchAtcPermissionUsers() // 新增：页面加载时拉取管制公示
  await fetchRankingsData() // 新增：获取排行榜数据
  fetchExternalLinks() // 新增：获取外链推荐数据
  startCountdownTimer() // 启动倒计时定时器
  nextTick(() => {
    initChart()
  })
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  if (durationChart) {
    durationChart.destroy()
  }
  stopCountdownTimer() // 停止倒计时定时器
  window.removeEventListener('resize', handleResize)
})
</script>

<style lang="scss" scoped>
@use "@/sass/theme.scss" as *;

.home-container {
  width: 100%;
  min-height: 100vh;
  background-color: var(--light-color);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  margin-top: 0; // 确保没有顶部空白
}

.tab-nav {
  width: 100%;
  background: var(--white-color);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  margin-bottom: var(--spacing-md);
  display: flex;
  gap: var(--spacing-sm);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  border: 1px solid var(--border-color);

  &::-webkit-scrollbar {
    display: none;
  }

  .tab-btn {
    padding: var(--spacing-sm) var(--spacing-lg);
    border: 1px solid var(--border-color);
    background: var(--white-color);
    color: var(--text-color);
    font-size: var(--font-size-base);
    cursor: pointer;
    white-space: nowrap;
    border-radius: var(--border-radius);
    transition: all 0.3s ease;

    &:hover {
      background-color: var(--light-color);
      border-color: var(--primary-color);
    }

    &.active {
      background-color: var(--primary-color);
      color: var(--white-color);
      border-color: var(--primary-color);
    }
  }
}
.announcement-full-bg-card {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}
.announcement-full-bg-img {
  position: absolute;
  left: 0; top: 0;
  width: 100%;
  height: 100%;
  min-height: 0;
  max-height: none;
  object-fit: cover;
  z-index: 1;
  border-radius: 10px;
}
.announcement-info.overlay-full {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  background: linear-gradient(0deg, rgba(0,0,0,0.7) 80%, rgba(0,0,0,0.1) 100%);
  color: #fff;
  padding: 18px 20px 12px 20px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  z-index: 2;
}
.announcement-info.overlay-full .news-title .title {
  color: #fff;
  font-size: 1.2em;
  font-weight: bold;
  text-shadow: 0 2px 8px rgba(0,0,0,0.3);
}
.announcement-info.overlay-full .news-meta {
  color: #eee;
  font-size: 0.98em;
  margin-top: 4px;
}

.rankings-external-section {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--spacing-md);
  width: 100%;
  margin-bottom: var(--spacing-md);
}

.rankings-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: var(--spacing-md);
  width: 100%;
  margin-bottom: var(--spacing-md);
}

.ranking-card {
  background: var(--white-color);
  border-radius: var(--border-radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: var(--spacing-md);
  height: 100%;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .card-header {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    padding: var(--spacing-md);
    border-bottom: 1px solid var(--border-color);
    background: var(--light-color);

    h3 {
      margin: 0;
      font-size: var(--font-size-lg);
      color: var(--text-color);
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);

      i {
        color: var(--primary-color);
        font-size: 1.2em;
      }
    }

    .tab-nav {
      display: flex;
      gap: var(--spacing-sm);
      align-items: center;

      &.ranking-tabs {
        margin-top: var(--spacing-xs);
      }

      &.time-tabs {
        margin-top: var(--spacing-xs);
        border-top: 1px solid var(--border-color);
        padding-top: var(--spacing-sm);
        flex-wrap: wrap;
        gap: var(--spacing-sm);
        
        .time-range-info {
          display: flex;
          align-items: center;
          margin-left: auto;
          
          .time-range-text {
            font-size: var(--font-size-sm);
            color: var(--text-light);
            background: var(--light-color);
            padding: var(--spacing-xs) var(--spacing-sm);
            border-radius: var(--border-radius);
            border: 1px solid var(--border-color);
            white-space: nowrap;
          }
        }
        
        .countdown-info {
          display: flex;
          align-items: center;
          
          .countdown-text {
            font-size: var(--font-size-sm);
            color: var(--primary-color);
            background: rgba(var(--primary-color-rgb), 0.1);
            padding: var(--spacing-xs) var(--spacing-sm);
            border-radius: var(--border-radius);
            border: 1px solid rgba(var(--primary-color-rgb), 0.3);
            white-space: nowrap;
            font-weight: 500;
          }
        }
      }

      .tab-btn {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        padding: var(--spacing-xs) var(--spacing-md);
        border: 1px solid var(--border-color);
        background: var(--white-color);
        color: var(--text-color);
        font-size: var(--font-size-sm);
        cursor: pointer;
        border-radius: var(--border-radius);
        transition: all 0.3s ease;

        i {
          font-size: 1em;
        }

        &:hover {
          background: var(--hover-color);
          border-color: var(--primary-color);
        }

        &.active {
          background: var(--primary-color);
          color: var(--white-color);
          border-color: var(--primary-color);
        }
      }
    }
  }

  .card-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    flex: 1;
    overflow-y: auto;
    padding-right: var(--spacing-sm);

    .chart-container {
      height: 300px;
      margin-bottom: var(--spacing-md);
      position: relative;
      overflow: hidden;
      background: var(--white-color);
      border-radius: var(--border-radius);
      border: 1px solid var(--border-color);
      padding: var(--spacing-md);
      transition: all 0.3s ease;

      canvas {
        animation: chartRise 1s ease-out;
      }
    }

    @keyframes chartRise {
      from {
        transform: translateY(100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    .ranking-list {
      .loading-rankings,
      .no-rankings {
        text-align: center;
        padding: var(--spacing-lg);
        color: var(--text-light);
        font-size: var(--font-size-sm);
      }

      .ranking-item {
        display: flex;
        align-items: center;
        padding: var(--spacing-sm);
        border-bottom: 1px solid var(--border-color);
        transition: all 0.3s ease;
        cursor: pointer;

        &:last-child {
          border-bottom: none;
        }

        &:hover {
          background: var(--light-color);
          transform: translateX(5px);
        }

        .rank-number {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--light-color);
          border-radius: 50%;
          font-size: var(--font-size-sm);
          color: var(--text-light);
          margin-right: var(--spacing-sm);
          transition: all 0.3s ease;

          &.top-three {
            background: var(--primary-color);
            color: var(--white-color);
            font-weight: bold;
          }
        }

        .user-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          margin-right: var(--spacing-sm);
          border: 2px solid var(--border-color);
        }

        .user-info {
          flex: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;

          .username {
            font-weight: 500;
            color: var(--text-color);
          }

          .score {
            font-size: var(--font-size-sm);
            color: var(--primary-color);
            background: rgba(var(--primary-rgb), 0.1);
            padding: 2px var(--spacing-xs);
            border-radius: var(--border-radius);
          }
        }
      }
    }
  }
}

// 教程外链区域样式
.tutorial-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: var(--spacing-md);
  width: 100%;
  margin-bottom: var(--spacing-md);
}

.tutorial-card {
  background: var(--white-color);
  border-radius: var(--border-radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: var(--spacing-md);
  height: 100%;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-md);
    border-bottom: 1px solid var(--border-color);
    background: var(--light-color);

    h3 {
      margin: 0;
      font-size: var(--font-size-lg);
      color: var(--text-color);
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);

      i {
        color: var(--primary-color);
        font-size: 1.2em;
      }
    }

    .view-all {
      color: var(--primary-color);
      text-decoration: none;
      font-size: var(--font-size-sm);
      padding: var(--spacing-xs) var(--spacing-sm);
      border-radius: var(--border-radius);
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);

      &:hover {
        background: rgba(var(--primary-rgb), 0.1);
      }

      i {
        font-size: 0.9em;
      }
    }
  }

  .card-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    flex: 1;
    padding: var(--spacing-md);

    .tutorial-iframe-container {
      flex: 1;
      min-height: 250px;
      border-radius: var(--border-radius);
      overflow: hidden;
      border: 1px solid var(--border-color);
      background: var(--light-color);
      position: relative;

      .tutorial-iframe {
        width: 100%;
        height: 100%;
        min-height: 250px;
        border: none;
        background: var(--white-color);
        transition: all 0.3s ease;

        &:hover {
          transform: scale(1.02);
        }
      }

      // 加载状态
      &::before {
        content: '正在加载教程内容...';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: var(--text-light);
        font-size: var(--font-size-sm);
        z-index: 1;
        pointer-events: none;
      }

      &:has(iframe[src])::before {
        display: none;
      }
    }

    .tutorial-info {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-sm);

      .tutorial-description {
        display: flex;
        align-items: flex-start;
        gap: var(--spacing-sm);
        color: var(--text-light);
        font-size: var(--font-size-sm);
        line-height: 1.5;

        i {
          color: var(--primary-color);
          margin-top: 2px;
          flex-shrink: 0;
        }
      }

      .tutorial-stats {
        display: flex;
        gap: var(--spacing-md);
        flex-wrap: wrap;

        .stat-item {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          font-size: var(--font-size-xs);
          color: var(--text-light);
          background: var(--light-color);
          padding: var(--spacing-xs) var(--spacing-sm);
          border-radius: var(--border-radius);
          border: 1px solid var(--border-color);

          i {
            color: var(--primary-color);
            font-size: 0.9em;
          }
        }
      }
    }
  }
}

// 外链推荐卡片样式
.external-links-card {
  background: var(--white-color);
  border-radius: var(--border-radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: var(--spacing-md);
  height: 100%;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-md);
    border-bottom: 1px solid var(--border-color);
    background: var(--light-color);

    h3 {
      margin: 0;
      font-size: var(--font-size-lg);
      color: var(--text-color);
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);

      i {
        color: var(--primary-color);
        font-size: 1.2em;
      }
    }

    .view-all {
      color: var(--primary-color);
      text-decoration: none;
      font-size: var(--font-size-sm);
      padding: var(--spacing-xs) var(--spacing-sm);
      border-radius: var(--border-radius);
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);

      &:hover {
        background: rgba(var(--primary-rgb), 0.1);
      }

      i {
        font-size: 0.9em;
      }
    }
  }

  .card-content {
    padding: var(--spacing-md);
    flex: 1;
    overflow-y: auto;

    .loading-external-links,
    .no-external-links {
      text-align: center;
      padding: var(--spacing-lg);
      color: var(--text-light);
      font-size: var(--font-size-sm);
    }

    .submit-link-btn {
      display: inline-flex;
      align-items: center;
      gap: var(--spacing-xs);
      margin-top: var(--spacing-md);
      padding: var(--spacing-sm) var(--spacing-md);
      background: var(--primary-color);
      color: var(--white-color);
      text-decoration: none;
      border-radius: var(--border-radius);
      font-size: var(--font-size-sm);
      transition: all 0.3s ease;

      &:hover {
        background: var(--primary-dark-color);
        transform: translateY(-1px);
      }

      i {
        font-size: 0.9em;
      }
    }

    .external-links-list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-sm);

      .external-link-item {
        padding: var(--spacing-sm);
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius);
        background: var(--white-color);
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          border-color: var(--primary-color);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transform: translateY(-1px);
        }

        .link-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-xs);

          .link-category {
            display: flex;
            align-items: center;
            gap: var(--spacing-xs);
            font-size: var(--font-size-xs);
            color: var(--primary-color);
            background: rgba(var(--primary-rgb), 0.1);
            padding: 2px var(--spacing-xs);
            border-radius: var(--border-radius);

            i {
              font-size: 0.8em;
            }
          }

          .link-points {
            display: flex;
            align-items: center;
            gap: var(--spacing-xs);
            font-size: var(--font-size-xs);
            color: var(--warning-color);
            background: rgba(var(--warning-rgb), 0.1);
            padding: 2px var(--spacing-xs);
            border-radius: var(--border-radius);

            i {
              font-size: 0.8em;
            }
          }
        }

        .link-title {
          font-weight: 600;
          color: var(--text-color);
          font-size: var(--font-size-sm);
          margin-bottom: var(--spacing-xs);
          line-height: 1.4;
        }

        .link-description {
          color: var(--text-light);
          font-size: var(--font-size-xs);
          line-height: 1.4;
          margin-bottom: var(--spacing-xs);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .link-meta {
          display: flex;
          gap: var(--spacing-sm);
          font-size: var(--font-size-xs);
          color: var(--text-light);

          .link-author {
            color: var(--primary-color);
            font-weight: 500;
          }

          .link-views {
            display: flex;
            align-items: center;
            gap: 2px;

            i {
              font-size: 0.8em;
            }
          }

          .link-time {
            margin-left: auto;
          }
        }
      }
    }
  }
}

.info-cards-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
  padding: var(--spacing-md);
}

.info-card {
  background: var(--white-color);
  border-radius: var(--border-radius);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-md);
    border-bottom: 1px solid var(--border-color);
    background: var(--light-color);

    h3 {
      margin: 0;
      font-size: var(--font-size-lg);
      color: var(--text-color);
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);

      i {
        color: var(--primary-color);
        font-size: 1.2em;
      }
    }

    .view-all {
      color: var(--primary-color);
      text-decoration: none;
      font-size: var(--font-size-sm);
      padding: var(--spacing-xs) var(--spacing-sm);
      border-radius: var(--border-radius);
      transition: all 0.3s ease;

      &:hover {
        background: rgba(var(--primary-rgb), 0.1);
      }
    }
  }

  .card-content {
    padding: var(--spacing-md);
    flex: 1;
    overflow-y: auto;
  }
}

.info-card.news-card .card-content {
  height: 100%;
  padding: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

// 最新公告样式
.news-item {
  padding: var(--spacing-sm) 0;
  border-bottom: 1px solid var(--border-color);
  transition: all 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--light-color);
    padding-left: var(--spacing-sm);
  }

  .news-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-xs);

    .title {
      font-weight: 500;
      color: var(--text-color);
      flex: 1;
      margin-right: var(--spacing-sm);
    }

    .date {
      font-size: var(--font-size-sm);
      color: var(--text-light);
      white-space: nowrap;
    }
  }

  .news-brief {
    color: var(--text-light);
    font-size: var(--font-size-sm);
    margin-bottom: var(--spacing-xs);
    line-height: 1.5;
  }

  .news-actions {
    display: flex;
    gap: var(--spacing-sm);
    margin-top: var(--spacing-xs);

    .expand-btn, .detail-link {
      font-size: var(--font-size-sm);
      color: var(--primary-color);
      background: none;
      border: none;
      cursor: pointer;
      padding: var(--spacing-xs) var(--spacing-sm);
      border-radius: var(--border-radius);
      transition: all 0.3s ease;

      &:hover {
        background: rgba(var(--primary-rgb), 0.1);
      }
    }
  }
}

// 最新活动样式
.event-item {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) 0;
  border-bottom: 1px solid var(--border-color);
  transition: all 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--light-color);
    padding-left: var(--spacing-sm);
  }

  .event-image {
    width: 120px;
    height: 80px;
    object-fit: cover;
    border-radius: var(--border-radius);
    border: 1px solid var(--border-color);
  }

  .event-info {
    flex: 1;

    .event-title {
      margin: 0 0 var(--spacing-xs);
      font-size: var(--font-size-base);
      color: var(--text-color);
      font-weight: 500;
    }

    .event-details {
      font-size: var(--font-size-sm);
      color: var(--text-light);

      p {
        margin: var(--spacing-xs) 0;
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);

        i {
          width: 16px;
          color: var(--primary-color);
        }
      }
    }

    .event-link {
      display: inline-block;
      margin-top: var(--spacing-xs);
      color: var(--primary-color);
      text-decoration: none;
      font-size: var(--font-size-sm);
      padding: var(--spacing-xs) var(--spacing-sm);
      border-radius: var(--border-radius);
      transition: all 0.3s ease;

      &:hover {
        background: rgba(var(--primary-rgb), 0.1);
      }
    }
  }
}

// 最新注册样式
.user-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) 0;
  border-bottom: 1px solid var(--border-color);
  transition: all 0.3s ease;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--light-color);
    padding-left: var(--spacing-sm);
    transform: translateX(4px);
  }

  .user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid var(--border-color);
  }

  .user-info {
    flex: 1;
    display: flex;
    flex-direction: column;

    .username {
      font-weight: 500;
      color: var(--text-color);
    }

    .user-level {
      font-size: var(--font-size-sm);
      color: var(--primary-color);
      background: rgba(var(--primary-rgb), 0.1);
      padding: 2px var(--spacing-xs);
      border-radius: var(--border-radius);
      display: inline-block;
      margin-top: var(--spacing-xs);
    }
  }

  .register-time {
    font-size: var(--font-size-sm);
    color: var(--text-light);
    white-space: nowrap;
  }
}

// 当前线上值班样式
.online-controllers-card {
  .card-header {
    .online-count {
      font-size: var(--font-size-sm);
      color: var(--primary-color);
      background: rgba(var(--primary-rgb), 0.1);
      padding: var(--spacing-xs) var(--spacing-sm);
      border-radius: var(--border-radius);
    }
  }

  .controller-item {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm) 0;
    border-bottom: 1px solid var(--border-color);
    transition: all 0.3s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: var(--light-color);
      padding-left: var(--spacing-sm);
    }

    .controller-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
      border: 2px solid var(--border-color);
    }

    .controller-info {
      flex: 1;

      .controller-name-role {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        margin-bottom: var(--spacing-xs);

        .name {
          font-weight: 500;
          color: var(--text-color);
        }

        .role {
          font-size: var(--font-size-sm);
          color: var(--primary-color);
          background: rgba(var(--primary-rgb), 0.1);
          padding: 2px var(--spacing-xs);
          border-radius: var(--border-radius);
        }
      }

      .controller-details {
        display: flex;
        gap: var(--spacing-md);
        font-size: var(--font-size-sm);
        color: var(--text-light);

        .icao {
          font-family: monospace;
          background: var(--light-color);
          padding: 2px var(--spacing-xs);
          border-radius: var(--border-radius);
        }
      }
    }

    .controller-status {
      font-size: var(--font-size-sm);
      padding: 2px var(--spacing-sm);
      border-radius: var(--border-radius);
      background: rgba(var(--success-rgb), 0.1);
      color: var(--success-color);
      white-space: nowrap;

      &.busy {
        background: rgba(var(--warning-rgb), 0.1);
        color: var(--warning-color);
      }
    }
  }
}

.scrollable {
  max-height: 350px;
  overflow-y: auto;
}

.announcement-carousel {
  width: 100%;
  .el-carousel__container {
    min-height: 180px;
    max-height: 240px;
  }
  .carousel-item-content {
    display: flex;
    align-items: center;
    gap: 20px;
    cursor: pointer;
    height: 100%;
    padding: 10px 0;
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 10px;
      padding: 0;
    }
  }
  .announcement-image {
    width: 220px;
    height: 140px;
    object-fit: cover;
    border-radius: 10px;
    border: 1px solid var(--border-color);
    @media (max-width: 768px) {
      width: 100%;
      height: 120px;
    }
  }
  .announcement-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    &.with-image {
      min-width: 0;
    }
    .news-title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;
      .title {
        font-weight: 600;
        font-size: 1.1em;
        color: var(--text-color);
        flex: 1;
        margin-right: 10px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
    .news-meta {
      font-size: 0.95em;
      color: var(--text-light);
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
    }
    .news-brief {
      color: var(--text-light);
      font-size: 0.98em;
      margin-top: 2px;
      line-height: 1.5;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }
}

.announcement-image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  overflow: hidden;
}
.announcement-image.full-bg {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.announcement-info.overlay {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  background: linear-gradient(0deg, rgba(0,0,0,0.7) 80%, rgba(0,0,0,0.1) 100%);
  color: #fff;
  padding: 18px 20px 12px 20px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  z-index: 2;
}
.announcement-info.overlay .news-title .title {
  color: #fff;
  font-size: 1.2em;
  font-weight: bold;
  text-shadow: 0 2px 8px rgba(0,0,0,0.3);
}
.announcement-info.overlay .news-meta {
  color: #eee;
  font-size: 0.98em;
  margin-top: 4px;
}

.announcement-img-card {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: stretch;
  background: #fff;
}
.news-title.top-title {
  padding: 0 20px 8px 20px; /* 顶部0，紧贴卡片顶部 */
  margin-top: 8px; /* 空一行 */
  font-size: 1.2em;
  font-weight: bold;
  color: var(--text-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.img-center-wrap {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  max-height: 220px;
  overflow: hidden;
}
.announcement-img-center {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  background: #f6f6f6;
}
.news-meta.bottom-meta {
  padding: 8px 20px 14px 20px;
  color: #888;
  font-size: 0.98em;
  display: flex;
  gap: 16px;
  justify-content: flex-start;
  align-items: center;
  flex-shrink: 0;
}
.announcement-carousel .el-carousel__indicators {
  position: absolute !important;
  bottom: 0 !important;
  left: 0;
  width: 100%;
  z-index: 10;
  background: none;
  margin-bottom: 4px;
  justify-content: center;
}

.announcement-noimg-card {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  /* 彻底撑满父容器 */
  display: block;
}
.announcement-info.noimg-full {
  width: 100%;
  height: 100%;
  padding: 24px 24px 18px 24px;
  box-sizing: border-box;
  display: block;
}

.announcement-card-flex {
  width: 100%;
  height: 100%;
  min-height: 180px;
  max-height: 240px;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: stretch;
}
.news-title.top-title {
  padding: 0 20px 8px 20px; /* 顶部0，紧贴卡片顶部 */
  margin-top: 8px; /* 空一行 */
  font-size: 1.2em;
  font-weight: bold;
  color: var(--text-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.card-content-center {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  max-height: 160px;
  overflow: hidden;
}
.announcement-img-center-flex {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  background: #f6f6f6;
}
.news-brief {
  color: #888;
  font-size: 1em;
  line-height: 1.5;
  text-align: left; /* 改为左对齐 */
  padding: 0 16px;
  word-break: break-all;
}
.news-meta.bottom-meta {
  padding: 8px 20px 14px 20px;
  color: #888;
  font-size: 0.98em;
  display: flex;
  gap: 16px;
  justify-content: flex-start;
  align-items: center;
  flex-shrink: 0;
}
.announcement-carousel .el-carousel__indicators {
  position: absolute !important;
  bottom: 0 !important;
  left: 0;
  width: 100%;
  z-index: 10;
  background: none;
  margin-bottom: 4px;
  justify-content: center;
}

.flex-col {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.card-content.flex-1 {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  position: relative;
}
.custom-carousel-indicators {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 8px 0 0 0;
  position: relative;
  z-index: 20;
}
.custom-indicator-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e0e0e0;
  display: inline-block;
  transition: background 0.2s;
}
.custom-indicator-dot.active {
  background: #409eff;
}

.permission-logs-card .permission-log-item {
  padding: 8px 0;
  border-bottom: 1px solid #e4e4e4;
  font-size: 1em;
}
.permission-logs-card .permission-log-item:last-child {
  border-bottom: none;
}

.atc-permission-card .card-header {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  justify-content: space-between;
  padding-right: 0;
  h3 {
    flex-shrink: 0;
    margin-right: 8px;
    white-space: nowrap;
  }
  .el-select {
    min-width: 130px;
    max-width: 150px;
    width: 130px;
    flex-shrink: 0;
    margin-left: auto;
  }
}
.atc-permission-card .card-content.scrollable {
  max-height: 220px;
  overflow-y: auto;
  padding: 12px 20px 12px 20px;
}
.atc-permission-user-item {
  display: flex;
  gap: 24px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e4e4e4;
  font-size: 1em;
}
.atc-permission-user-item:last-child {
  border-bottom: none;
}
.no-atc-permission-users {
  color: #aaa;
  text-align: center;
  padding: 32px 0;
}

// 徽章变更公示样式
.badge-change-card .badge-change-log-item {
  padding: 12px 0;
  border-bottom: 1px solid #e4e4e4;
  font-size: 0.95em;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--light-color);
    padding-left: 8px;
    transform: translateX(4px);
  }
}
.badge-change-card .badge-change-log-item:last-child {
  border-bottom: none;
}
.badge-change-card .badge-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.badge-change-card .badge-icon-small {
  width: 20px;
  height: 20px;
  object-fit: contain;
  border-radius: 4px;
}
.badge-change-card .badge-name {
  font-weight: 500;
  color: var(--text-color);
  font-size: 0.9em;
}
.badge-change-card .change-detail {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.badge-change-card .badge-change-meta {
  color: #aaa;
  font-size: 0.85em;
  flex-shrink: 0;
  white-space: nowrap;
}
.badge-change-card .target-user {
  font-weight: 500;
  color: var(--primary-color);
}
.badge-change-card .action-type {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: 500;
}
.badge-change-card .action-type.assign {
  background: rgba(103, 194, 58, 0.1);
  color: #67c23a;
}
.badge-change-card .action-type.revoke {
  background: rgba(245, 108, 108, 0.1);
  color: #f56c6c;
}
.badge-change-card .operator {
  color: var(--text-light);
  font-size: 0.85em;
}

.no-badge-change-logs {
  color: #aaa;
  text-align: center;
  padding: 32px 0;
}

// 处罚公示样式
.penalty-card {
  .card-header h3 i {
    color: #f56c6c; // 警告色
  }
}

.no-penalty-logs {
  color: #aaa;
  text-align: center;
  padding: 32px 0;
}

// 活动日历样式
.events-calendar-card {
  .card-header {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    padding: var(--spacing-md);
    border-bottom: 1px solid var(--border-color);
    background: var(--light-color);
    
    .header-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      
      h3 {
        margin: 0;
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        
        i {
          color: var(--primary-color);
        }
      }
      
      .calendar-view-tabs {
        display: flex;
        gap: 2px;
        
        .view-tab {
          padding: var(--spacing-xs) var(--spacing-sm);
          border: 1px solid var(--border-color);
          background: var(--white-color);
          color: var(--text-color);
          border-radius: var(--border-radius);
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: var(--font-size-xs);
          min-width: 40px;
          text-align: center;
          
          &:hover {
            background: var(--hover-color);
            border-color: var(--primary-color);
          }
          
          &.active {
            background: var(--primary-color);
            color: var(--white-color);
            border-color: var(--primary-color);
          }
        }
      }
    }
    
    .calendar-controls {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--spacing-sm);
      
      .calendar-nav-btn {
        background: var(--white-color);
        border: 1px solid var(--border-color);
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        
        &:hover {
          background: var(--primary-color);
          border-color: var(--primary-color);
          color: var(--white-color);
        }
        
        i {
          font-size: 0.8em;
        }
      }
      
      .current-period {
        font-weight: 500;
        color: var(--text-color);
        min-width: 100px;
        text-align: center;
        font-size: var(--font-size-sm);
      }
    }
  }
  
  .card-content {
    padding: 0;
    max-height: 300px;
    overflow-y: auto;
    position: relative;
    z-index: 1;
    
    // 自定义滚动条
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: var(--light-color);
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: var(--border-color);
      border-radius: 3px;
      
      &:hover {
        background: var(--text-light);
      }
    }
    
    // 自定义滚动条
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: var(--light-color);
    }
    
    &::-webkit-scrollbar-thumb {
      background: var(--border-color);
      border-radius: 3px;
      
      &:hover {
        background: var(--text-light);
      }
    }
    
    // 周视图样式
    .calendar-week-view {
      .week-header {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 1px;
        position: sticky;
        top: 0;
        background: var(--white-color);
        z-index: 10;
        border-bottom: 2px solid var(--border-color);
        
        .week-day-header {
          text-align: center;
          padding: var(--spacing-sm);
          background: var(--light-color);
          
          .day-name {
            font-weight: 600;
            color: var(--text-color);
            font-size: var(--font-size-sm);
            margin-bottom: 2px;
          }
          
          .day-date {
            color: var(--text-light);
            font-size: var(--font-size-xs);
          }
        }
      }
      
      .week-grid {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 1px;
        
                  .week-day-cell {
            border-right: 1px solid var(--border-color);
            border-bottom: 1px solid var(--border-color);
            padding: var(--spacing-sm);
            min-height: 60px;
            background: var(--white-color);
            position: relative;
            cursor: pointer;
            transition: all 0.3s ease;
            overflow: visible;
            z-index: 1;
            
            &:nth-child(7n) {
              border-right: none;
            }
            
            &:hover {
              background: var(--light-color);
              transform: translateY(-1px);
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              z-index: 10;
            }
            
            &.has-events {
              background: rgba(64, 158, 255, 0.05);
              
              &:hover {
                background: rgba(64, 158, 255, 0.1);
              }
              
              &.has-upcoming {
                background: rgba(64, 158, 255, 0.08);
                border-left: 3px solid #409eff;
                
                &:hover {
                  background: rgba(64, 158, 255, 0.15);
                }
              }
              
              &.has-ongoing {
                background: rgba(103, 194, 58, 0.08);
                border-left: 3px solid #67c23a;
                
                &:hover {
                  background: rgba(103, 194, 58, 0.15);
                }
              }
              
              &.has-ended {
                background: rgba(245, 108, 108, 0.08);
                border-left: 3px solid #f56c6c;
                
                &:hover {
                  background: rgba(245, 108, 108, 0.15);
                }
              }
            }
            
            .day-content {
              display: flex;
              flex-direction: column;
              height: 100%;
              
              .event-star {
                position: absolute;
                top: 2px;
                right: 2px;
                color: #ffd700 !important;
                font-size: 12px;
                z-index: 10;
                text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
              }
              
              .day-number {
                font-weight: 600;
                font-size: var(--font-size-sm);
                color: var(--text-color);
                margin-bottom: var(--spacing-xs);
              }
              
              .event-indicators {
                position: relative;
                
                .event-tooltip {
                  position: absolute;
                  top: -120px;
                  left: 50%;
                  transform: translateX(-50%);
                  background: rgba(0, 0, 0, 0.95);
                  color: white;
                  padding: 10px 14px;
                  border-radius: 8px;
                  font-size: 12px;
                  z-index: 999999;
                  opacity: 0;
                  visibility: hidden;
                  transition: opacity 0.3s ease;
                  pointer-events: none;
                  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
                  width: 300px;
                  white-space: normal;
                  
                  &::after {
                    content: '';
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    border: 6px solid transparent;
                    border-top-color: rgba(0, 0, 0, 0.95);
                  }
                  
                  .event-tooltip-item {
                    margin: 4px 0;
                    line-height: 1.4;
                    
                    .event-title {
                      font-weight: 600;
                      margin-bottom: 2px;
                      color: #fff;
                    }
                    
                    .event-time {
                      font-size: 11px;
                      color: #ccc;
                    }
                  }
                }
                
                &:hover .event-tooltip {
                  opacity: 1;
                  visibility: visible;
                }
              }
              

            }
          }
      }
    }
    
    // 月视图样式
    .calendar-month-view {
      .month-grid {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 1px;
        
        .month-day-cell {
          border-right: 1px solid var(--border-color);
          border-bottom: 1px solid var(--border-color);
          padding: var(--spacing-xs);
          min-height: 50px;
          background: var(--white-color);
          position: relative;
          cursor: pointer;
          transition: all 0.3s ease;
          overflow: visible;
          z-index: 1;
          
          &:nth-child(7n) {
            border-right: none;
          }
          
          &:hover {
            background: var(--light-color);
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            z-index: 10;
          }
          
          &.other-month {
            background: var(--light-color);
            color: var(--text-light);
            
            &:hover {
              background: var(--border-color);
            }
          }
          
          &.today {
            background: linear-gradient(135deg, rgba(64, 158, 255, 0.1), rgba(64, 158, 255, 0.05));
            border: 2px solid var(--primary-color);
          }
          
          &.has-events {
            background: rgba(64, 158, 255, 0.05);
            
            &:hover {
              background: rgba(64, 158, 255, 0.1);
            }
            
            &.has-upcoming {
              background: rgba(64, 158, 255, 0.08);
              border-left: 3px solid #409eff;
              
              &:hover {
                background: rgba(64, 158, 255, 0.15);
              }
            }
            
            &.has-ongoing {
              background: rgba(103, 194, 58, 0.08);
              border-left: 3px solid #67c23a;
              
              &:hover {
                background: rgba(103, 194, 58, 0.15);
              }
            }
            
            &.has-ended {
              background: rgba(245, 108, 108, 0.08);
              border-left: 3px solid #f56c6c;
              
              &:hover {
                background: rgba(245, 108, 108, 0.15);
              }
            }
          }
          
            .day-content {
              display: flex;
              flex-direction: column;
              height: 100%;
              
              .event-star {
                position: absolute;
                top: 1px;
                right: 1px;
                color: #ffd700 !important;
                font-size: 10px;
                z-index: 10;
                text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
              }
              
              .event-indicators {
                position: relative;
              
              .event-tooltip {
                position: absolute;
                top: -100px;
                left: 50%;
                transform: translateX(-50%);
                background: rgba(0, 0, 0, 0.95);
                color: white;
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 11px;
                z-index: 999999;
                opacity: 0;
                visibility: hidden;
                transition: opacity 0.3s ease;
                pointer-events: none;
                box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
                width: 280px;
                white-space: normal;
                
                &::after {
                  content: '';
                  position: absolute;
                  top: 100%;
                  left: 50%;
                  transform: translateX(-50%);
                  border: 5px solid transparent;
                  border-top-color: rgba(0, 0, 0, 0.95);
                }
                
                .event-tooltip-item {
                  margin: 3px 0;
                  line-height: 1.3;
                  
                  .event-title {
                    font-weight: 600;
                    margin-bottom: 2px;
                    color: #fff;
                    font-size: 11px;
                  }
                  
                  .event-time {
                    font-size: 10px;
                    color: #ccc;
                  }
                }
              }
              
              &:hover .event-tooltip {
                opacity: 1;
                visibility: visible;
              }
            }
            

          }
        }
      }
    }
    
    // 年视图样式
    .calendar-year-view {
      .year-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--spacing-sm);
        padding: var(--spacing-sm);
        
        .year-month-cell {
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius);
          padding: var(--spacing-sm);
          background: var(--white-color);
          min-height: 60px;
          
          .month-name {
            font-weight: 600;
            color: var(--text-color);
            margin-bottom: var(--spacing-sm);
            text-align: center;
            font-size: var(--font-size-sm);
            padding-bottom: var(--spacing-xs);
            border-bottom: 1px solid var(--border-color);
          }
          
          .month-days {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 1px;
            
            .year-day-cell {
              padding: 2px;
              text-align: center;
              position: relative;
              cursor: pointer;
              transition: all 0.3s ease;
              border-radius: 2px;
              overflow: visible;
              z-index: 1;
              
              &:hover {
                background: var(--light-color);
                transform: scale(1.1);
                z-index: 10;
              }
              
              &.has-events {
                background: rgba(64, 158, 255, 0.05);
                
                &:hover {
                  background: rgba(64, 158, 255, 0.1);
                }
                
                &.has-upcoming {
                  background: rgba(64, 158, 255, 0.08);
                  border-left: 2px solid #409eff;
                  
                  &:hover {
                    background: rgba(64, 158, 255, 0.15);
                  }
                }
                
                &.has-ongoing {
                  background: rgba(103, 194, 58, 0.08);
                  border-left: 2px solid #67c23a;
                  
                  &:hover {
                    background: rgba(103, 194, 58, 0.15);
                  }
                }
                
                &.has-ended {
                  background: rgba(245, 108, 108, 0.08);
                  border-left: 2px solid #f56c6c;
                  
                  &:hover {
                    background: rgba(245, 108, 108, 0.15);
                  }
                }
              }
              
              .day-number {
                font-size: var(--font-size-xs);
                color: var(--text-color);
                margin-bottom: 2px;
              }
              
              .event-star {
                position: absolute;
                top: 0;
                right: 0;
                color: #ffd700 !important;
                font-size: 8px;
                z-index: 10;
                text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
              }
              
              .event-indicators {
                position: relative;
                
                .event-tooltip {
                  position: absolute;
                  top: -80px;
                  left: 50%;
                  transform: translateX(-50%);
                  background: rgba(0, 0, 0, 0.95);
                  color: white;
                  padding: 6px 10px;
                  border-radius: 4px;
                  font-size: 10px;
                  z-index: 999999;
                  opacity: 0;
                  visibility: hidden;
                  transition: opacity 0.3s ease;
                  pointer-events: none;
                  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
                  width: 250px;
                  white-space: normal;
                  
                  &::after {
                    content: '';
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    border: 4px solid transparent;
                    border-top-color: rgba(0, 0, 0, 0.95);
                  }
                  
                  .event-tooltip-item {
                    margin: 2px 0;
                    line-height: 1.2;
                    
                    .event-title {
                      font-weight: 600;
                      margin-bottom: 1px;
                      color: #fff;
                      font-size: 10px;
                    }
                    
                    .event-time {
                      font-size: 9px;
                      color: #ccc;
                    }
                  }
                }
                
                &:hover .event-tooltip {
                  opacity: 1;
                  visibility: visible;
                }
              }
              

            }
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .home-container {
    padding: var(--spacing-sm);
  }

  .rankings-external-section {
    grid-template-columns: 1fr;
  }

  .rankings-section {
    grid-template-columns: 1fr;
  }

  .ranking-card {
    min-height: 400px;
  }

  .external-links-card {
    min-height: 350px;
  }

  .tutorial-section {
    grid-template-columns: 1fr;
  }

  .tutorial-card {
    min-height: 350px;
    
    .card-content {
      .tutorial-iframe-container {
        min-height: 200px;
        
        .tutorial-iframe {
          min-height: 200px;
        }
      }
      
      .tutorial-info {
        .tutorial-stats {
          flex-direction: column;
          gap: var(--spacing-sm);
        }
      }
    }
  }

  .chart-container {
    min-height: 200px;
  }

  .info-cards-section {
    grid-template-columns: 1fr;
    padding: var(--spacing-sm);
  }

  .event-item {
    flex-direction: column;

    .event-image {
      width: 100%;
      height: 160px;
    }
  }

  .controller-item {
    .controller-info {
      .controller-details {
        flex-direction: column;
        gap: var(--spacing-xs);
      }
    }
  }

  .card-header {
    .tab-nav {
      flex-wrap: wrap;
      
      .tab-btn {
        flex: 1;
        min-width: 120px;
        justify-content: center;
      }
    }
  }
  .announcement-carousel {
    .carousel-item-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
    .announcement-image {
      width: 100%;
      height: 120px;
    }
    .announcement-info {
      width: 100%;
      .news-title .title {
        font-size: 1em;
      }
    }
  }
}
</style> 